// src/components/Booking/BookingCalendarPopup.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import "../../styles/booking-calendar.css";

const pad = (n) => String(n).padStart(2, "0");
const toKey = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseKey = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * availableKeys: array of "YYYY-MM-DD" strings that can be booked
 * selectedKey:   currently chosen "YYYY-MM-DD" (or "")
 * onSelect(key): called when a highlighted day is clicked
 */
const BookingCalendarPopup = ({
  availableKeys,
  selectedKey,
  accentColor,
  onSelect,
  onClose,
}) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const available = useMemo(() => new Set(availableKeys), [availableKeys]);
  const sorted = useMemo(() => [...availableKeys].sort(), [availableKeys]);
  const lastMonth = sorted.length
    ? monthStart(parseKey(sorted[sorted.length - 1]))
    : null;

  const [viewMonth, setViewMonth] = useState(() =>
    monthStart(
      selectedKey
        ? parseKey(selectedKey)
        : sorted.length
          ? parseKey(sorted[0])
          : today,
    ),
  );

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cells = useMemo(() => {
    const y = viewMonth.getFullYear();
    const m = viewMonth.getMonth();
    const offset = (new Date(y, m, 1).getDay() + 6) % 7; // Monday first
    const days = new Date(y, m + 1, 0).getDate();
    return [
      ...Array(offset).fill(null),
      ...Array.from({ length: days }, (_, i) => new Date(y, m, i + 1)),
    ];
  }, [viewMonth]);

  const canGoPrev = viewMonth > monthStart(today);
  const canGoNext = lastMonth && viewMonth < lastMonth;
  const shift = (delta) =>
    setViewMonth(
      new Date(viewMonth.getFullYear(), viewMonth.getMonth() + delta, 1),
    );

  return createPortal(
    <div
      className="bc-overlay"
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bc-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Choose a date"
        style={accentColor ? { "--bc-accent": accentColor } : undefined}
      >
        <div className="bc-header">
          <h2>Choose a date</h2>
          <button
            type="button"
            className="bc-close"
            onClick={onClose}
            aria-label="Close calendar"
          >
            <FiX />
          </button>
        </div>

        <div className="bc-body">
          <div className="bc-month-nav">
            <button
              type="button"
              onClick={() => shift(-1)}
              disabled={!canGoPrev}
              aria-label="Previous month"
            >
              <FiChevronLeft />
            </button>
            <strong>
              {viewMonth.toLocaleDateString("en-ZA", {
                month: "long",
                year: "numeric",
              })}
            </strong>
            <button
              type="button"
              onClick={() => shift(1)}
              disabled={!canGoNext}
              aria-label="Next month"
            >
              <FiChevronRight />
            </button>
          </div>

          <div className="bc-weekdays">
            {WEEKDAYS.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>

          <div className="bc-grid">
            {cells.map((date, i) => {
              if (!date) return <span key={`blank-${i}`} />;
              const key = toKey(date);
              const open = date >= today && available.has(key);
              const cls = [
                "bc-day",
                open ? "is-open" : "is-closed",
                key === selectedKey ? "is-selected" : "",
                date.getTime() === today.getTime() ? "is-today" : "",
              ].join(" ");
              return (
                <button
                  type="button"
                  key={key}
                  className={cls}
                  disabled={!open}
                  onClick={() => onSelect(key)}
                  aria-label={`${date.toLocaleDateString("en-ZA", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}${open ? "" : ", unavailable"}`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="bc-legend">
            <span>
              <i className="dot open" /> Available
            </span>
            <span>
              <i className="dot closed" /> Unavailable
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BookingCalendarPopup;
